import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from '@/components/ui/Spinner';
import { flipkartAdsAccountsQueryKey } from '@/components/FlipkartAdsOauthConnections';
import { flipkartAdsApi } from '@/api/client';
import {
  flipkartAdAccountLabel,
  parseFlipkartLegacyCampaignPath,
  workspaceCampaignPath,
} from '@/api/workspaceScope';

export function FlipkartLegacyCampaignRedirect() {
  const location = useLocation();
  const { credentialId: credentialIdParam } = useParams<{ credentialId: string }>();
  const credentialId = credentialIdParam ? decodeURIComponent(credentialIdParam) : '';
  const legacy = parseFlipkartLegacyCampaignPath(location.pathname);

  const accountsQuery = useQuery({
    queryKey: flipkartAdsAccountsQueryKey(credentialId),
    queryFn: async () => {
      const res = await flipkartAdsApi.listAccounts(credentialId);
      if (!res.success) throw new Error('Failed to load Flipkart ad accounts');
      return res.data.accounts;
    },
    enabled: Boolean(credentialId),
  });

  if (!credentialId) {
    return <Navigate to="/connections" replace />;
  }

  if (accountsQuery.isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  const accounts = [...(accountsQuery.data ?? [])].sort((a, b) =>
    flipkartAdAccountLabel(a).localeCompare(flipkartAdAccountLabel(b))
  );
  const first = accounts[0];
  if (!first) {
    return <Navigate to="/connections" replace />;
  }

  return (
    <Navigate
      to={workspaceCampaignPath(
        {
          platform: 'flipkart',
          credentialId,
          accountId: first.ad_account_id,
        },
        legacy?.tab ?? 'dashboard'
      )}
      replace
    />
  );
}
